import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Clock, DoorOpen, MapPin } from "lucide-react";
import { Status } from "../components/ui";
import { formatDayDate, formatTimeRange } from "../lib/datetime";
import { localizedTitle } from "../lib/localize";
import { TeacherPDDayItemRow } from "../pddays/TeacherPDDayItemRow";
import { useTeacherPDDaySummary } from "../pddays/useTeacherPDDaySummary";
import { usePDDayNumber } from "../pddays/usePDDays";

// The teacher-facing counterpart to the facilitator's live progress screen:
// a read-only look back at one PD day, scoped to this teacher's own
// completion — never the roster-wide counts facilitatorProgress exposes.
export function PDDaySummary() {
  const { t, i18n } = useTranslation();
  const { sessionID = "" } = useParams();
  const { data, loading, error } = useTeacherPDDaySummary(sessionID);
  const dayNumber = usePDDayNumber(sessionID);

  if (loading) {
    return <Status variant="loading" title={t("loading")} message={t("loadingProgress")} />;
  }
  if (error || !data) {
    return (
      <>
        <Status
          variant="error"
          title={t("progressUnavailable")}
          message={error || t("noProgress")}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-6">
      <div className="flex flex-col">
        <h1 className="text-xl font-semibold tracking-tight text-app-ink">
          {dayNumber ? t("pdDaySummaryOrdinal", { number: dayNumber }) : t("pdDaySummaryTitle")}
        </h1>
      </div>

      <div className="flex flex-col gap-2 rounded-xl bg-surface p-4 shadow-raised">
        <strong className="text-base font-semibold text-app-ink">
          {formatDayDate(data.start, i18n.language)}
        </strong>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-app-muted">
          <span className="inline-flex items-center gap-1" dir="ltr">
            <Clock size={14} />
            {formatTimeRange(data.start, data.end, i18n.language)}
          </span>
          {data.room && (
            <span className="inline-flex items-center gap-1">
              <DoorOpen size={14} />
              {data.room}
            </span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-sm text-app-copy">
          <MapPin size={14} className="shrink-0 text-app-icon" />
          <span className="truncate">{data.center}</span>
        </span>
      </div>

      {data.modules.length === 0 && (
        <p className="rounded-xl bg-surface p-4 text-sm text-app-muted shadow-raised">
          {t("noModulesYet")}
        </p>
      )}
      {data.modules.map((module) => (
        <section
          key={module.id}
          className="flex flex-col gap-1 rounded-xl bg-surface p-4 shadow-raised"
        >
          <h2 className="mb-2 text-base font-semibold text-app-ink">
            {data.modules.length > 1
              ? localizedTitle(module, i18n.language)
              : t("moduleCompletion")}
          </h2>
          {module.items.length === 0 ? (
            <p className="text-sm text-app-muted">{t("noCurriculum")}</p>
          ) : (
            <div className="divide-y divide-app-line">
              {module.items.map((item) => (
                <TeacherPDDayItemRow key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
